/**
 * Correlation ranking utilities for the Correlogram column selector.
 *
 * Provides relationship detectors (linear, quadratic, cubic, exponential)
 * and a column-level "interestingness" ranking that aggregates pairwise
 * scores to pick the best N columns to display.
 */

import { pearsonCorrelation, partialCorrelation, multipleCorrelationR } from "./useHistogramBins";

export type RelationshipType = "linear" | "quadratic" | "cubic" | "exponential";
export type CorrelogramSelectionMode =
  | "interesting"
  | "random"
  | "outliers"
  | "linear"
  | "quadratic"
  | "cubic"
  | "exponential";

export interface ColumnDef {
  field: string;
  headerName?: string;
}

export interface TargetCorrelationScore {
  field: string;
  label?: string;
  r: number;
  score: number;
  count: number;
}

// ---------------------------------------------------------------------------
// Constant-column detection
// ---------------------------------------------------------------------------

function extractFinite(rows: Record<string, unknown>[], key: string): number[] {
  const out: number[] = [];
  for (const r of rows) {
    const v = r[key];
    if (typeof v === "number" && Number.isFinite(v)) out.push(v);
  }
  return out;
}

export function isConstantColumn(rows: Record<string, unknown>[], key: string): boolean {
  const vals = extractFinite(rows, key);
  if (vals.length < 2) return true;
  const first = vals[0]!;
  return vals.every(v => v === first);
}

export function filterConstantColumns(
  rows: Record<string, unknown>[],
  columns: ColumnDef[],
): ColumnDef[] {
  return columns.filter(c => !isConstantColumn(rows, c.field));
}

// ---------------------------------------------------------------------------
// Pairwise relationship scoring
// ---------------------------------------------------------------------------

function extractAlignedPairs(
  rows: Record<string, unknown>[],
  xKey: string,
  yKey: string,
): { x: number[]; y: number[] } {
  const xs: number[] = [];
  const ys: number[] = [];
  for (const r of rows) {
    const xv = r[xKey];
    const yv = r[yKey];
    if (
      typeof xv === "number" && Number.isFinite(xv) &&
      typeof yv === "number" && Number.isFinite(yv)
    ) {
      xs.push(xv);
      ys.push(yv);
    }
  }
  return { x: xs, y: ys };
}

/** R^2 of a linear fit (Pearson^2). */
function linearR2(x: number[], y: number[]): number {
  const r = pearsonCorrelation(x, y);
  return Number.isFinite(r) ? r * r : 0;
}

/**
 * R^2 of a quadratic fit y = a + bx + cx^2.
 * Solves the 3x3 normal equations (Vandermonde).
 */
function quadraticR2(x: number[], y: number[]): number {
  const n = x.length;
  if (n < 4) return 0;

  let sx = 0, sx2 = 0, sx3 = 0, sx4 = 0;
  let sy = 0, sxy = 0, sx2y = 0;
  for (let i = 0; i < n; i++) {
    const xi = x[i]!;
    const yi = y[i]!;
    const x2 = xi * xi;
    sx += xi;
    sx2 += x2;
    sx3 += x2 * xi;
    sx4 += x2 * x2;
    sy += yi;
    sxy += xi * yi;
    sx2y += x2 * yi;
  }

  // Normal equations: M * [a, b, c]^T = rhs
  // M = [[n, sx, sx2], [sx, sx2, sx3], [sx2, sx3, sx4]]
  // Solve via Cramer's rule
  const det =
    n * (sx2 * sx4 - sx3 * sx3) -
    sx * (sx * sx4 - sx3 * sx2) +
    sx2 * (sx * sx3 - sx2 * sx2);

  if (Math.abs(det) < 1e-30) return 0;

  const a = (
    sy * (sx2 * sx4 - sx3 * sx3) -
    sx * (sxy * sx4 - sx2y * sx3) +
    sx2 * (sxy * sx3 - sx2y * sx2)
  ) / det;

  const b = (
    n * (sxy * sx4 - sx2y * sx3) -
    sy * (sx * sx4 - sx3 * sx2) +
    sx2 * (sx * sx2y - sxy * sx2)
  ) / det;

  const c = (
    n * (sx2 * sx2y - sx3 * sxy) -
    sx * (sx * sx2y - sxy * sx2) +
    sy * (sx * sx3 - sx2 * sx2)
  ) / det;

  const yMean = sy / n;
  let ssRes = 0, ssTot = 0;
  for (let i = 0; i < n; i++) {
    const xi = x[i]!;
    const yi = y[i]!;
    const pred = a + b * xi + c * xi * xi;
    ssRes += (yi - pred) ** 2;
    ssTot += (yi - yMean) ** 2;
  }

  return ssTot > 0 ? Math.max(0, 1 - ssRes / ssTot) : 0;
}

/**
 * R^2 of a cubic fit y = a + bx + cx^2 + dx^3.
 * Uses Gaussian elimination on the 4x4 normal equations. Inputs are
 * standardized first so large engineering units do not explode x^6 terms.
 */
function cubicR2(x: number[], y: number[]): number {
  const n = x.length;
  if (n < 5) return 0;

  const xMean = x.reduce((sum, value) => sum + value, 0) / n;
  const variance = x.reduce((sum, value) => sum + (value - xMean) ** 2, 0) / n;
  const xScale = Math.sqrt(variance) || 1;
  const xs = x.map((value) => (value - xMean) / xScale);

  const matrix = Array.from({ length: 4 }, () => Array.from({ length: 5 }, () => 0));
  for (let row = 0; row < n; row++) {
    const xi = xs[row]!;
    const basis = [1, xi, xi * xi, xi * xi * xi];
    for (let r = 0; r < 4; r++) {
      const matrixRow = matrix[r]!;
      for (let c = 0; c < 4; c++) {
        matrixRow[c] = matrixRow[c]! + basis[r]! * basis[c]!;
      }
      matrixRow[4] = matrixRow[4]! + basis[r]! * y[row]!;
    }
  }

  for (let pivot = 0; pivot < 4; pivot++) {
    let best = pivot;
    for (let row = pivot + 1; row < 4; row++) {
      if (Math.abs(matrix[row]![pivot]!) > Math.abs(matrix[best]![pivot]!)) best = row;
    }
    if (Math.abs(matrix[best]![pivot]!) < 1e-12) return 0;
    if (best !== pivot) [matrix[pivot], matrix[best]] = [matrix[best]!, matrix[pivot]!];

    const pivotRow = matrix[pivot]!;
    const divisor = pivotRow[pivot]!;
    for (let col = pivot; col < 5; col++) {
      pivotRow[col] = pivotRow[col]! / divisor;
    }

    for (let row = 0; row < 4; row++) {
      if (row === pivot) continue;
      const targetRow = matrix[row]!;
      const factor = targetRow[pivot]!;
      for (let col = pivot; col < 5; col++) {
        targetRow[col] = targetRow[col]! - factor * pivotRow[col]!;
      }
    }
  }

  const coeffs = matrix.map((row) => row[4]!);
  const yMean = y.reduce((sum, value) => sum + value, 0) / n;
  let ssRes = 0;
  let ssTot = 0;
  for (let i = 0; i < n; i++) {
    const xi = xs[i]!;
    const pred = coeffs[0]! + coeffs[1]! * xi + coeffs[2]! * xi * xi + coeffs[3]! * xi * xi * xi;
    ssRes += (y[i]! - pred) ** 2;
    ssTot += (y[i]! - yMean) ** 2;
  }

  return ssTot > 0 ? Math.max(0, Math.min(1, 1 - ssRes / ssTot)) : 0;
}

/**
 * R^2 of an exponential fit y = a * e^(bx).
 * Linearized: ln(y) = ln(a) + bx, using only positive y values.
 */
function exponentialR2(x: number[], y: number[]): number {
  const xf: number[] = [];
  const lny: number[] = [];
  for (let i = 0; i < x.length; i++) {
    if (y[i]! > 0) {
      xf.push(x[i]!);
      lny.push(Math.log(y[i]!));
    }
  }
  if (xf.length < 3) return 0;
  const r = pearsonCorrelation(xf, lny);
  return Number.isFinite(r) ? r * r : 0;
}

const SCORERS: Record<RelationshipType, (x: number[], y: number[]) => number> = {
  linear: linearR2,
  quadratic: quadraticR2,
  cubic: cubicR2,
  exponential: exponentialR2,
};

// ---------------------------------------------------------------------------
// Pair relationship summary
// ---------------------------------------------------------------------------

export type PairBestFit = "linear" | "quadratic" | "cubic" | "outliers" | "constant" | "weak";

export interface PairRelationshipScore {
  linearR2: number;
  quadraticR2: number;
  cubicR2: number;
  nonlinearLift: number;
  outlierScore: number;
  flatLineScore: number;
  uninteresting: boolean;
  bestFit: PairBestFit;
  score: number;
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(1, value));
}

function residualOutlierScore(x: number[], y: number[]): number {
  const n = x.length;
  if (n < 5) return 0;
  const xMean = x.reduce((sum, value) => sum + value, 0) / n;
  const yMean = y.reduce((sum, value) => sum + value, 0) / n;
  let sxx = 0;
  let sxy = 0;
  for (let i = 0; i < n; i++) {
    const dx = x[i]! - xMean;
    sxx += dx * dx;
    sxy += dx * (y[i]! - yMean);
  }
  const slope = sxx > 0 ? sxy / sxx : 0;
  const intercept = yMean - slope * xMean;
  return outlierScore(y.map((value, i) => value - (intercept + slope * x[i]!)));
}

function valueBucket(value: number): string {
  if (Object.is(value, -0)) return "0";
  return value.toPrecision(12);
}

function distributionShape(values: number[]): {
  uniqueCount: number;
  uniqueRatio: number;
  dominantRatio: number;
} {
  if (values.length === 0) return { uniqueCount: 0, uniqueRatio: 0, dominantRatio: 0 };
  const counts = new Map<string, number>();
  let dominant = 0;
  for (const value of values) {
    const key = valueBucket(value);
    const next = (counts.get(key) ?? 0) + 1;
    counts.set(key, next);
    if (next > dominant) dominant = next;
  }
  return {
    uniqueCount: counts.size,
    uniqueRatio: counts.size / values.length,
    dominantRatio: dominant / values.length,
  };
}

function flatLinePairScore(x: number[], y: number[]): number {
  const xShape = distributionShape(x);
  const yShape = distributionShape(y);
  const xNearlyConstant = xShape.uniqueCount <= 2 || xShape.dominantRatio >= 0.82;
  const yNearlyConstant = yShape.uniqueCount <= 2 || yShape.dominantRatio >= 0.82;
  const bothSmallPalettes = xShape.uniqueCount <= 12 && yShape.uniqueCount <= 12;
  const bothRepeatedAxes =
    xShape.uniqueCount <= 40 &&
    yShape.uniqueCount <= 40 &&
    xShape.uniqueRatio <= 0.06 &&
    yShape.uniqueRatio <= 0.06;
  const oneRepeatedAgainstSmallPalette =
    (xShape.uniqueCount <= 4 && yShape.uniqueRatio <= 0.08 && yShape.uniqueCount <= 60) ||
    (yShape.uniqueCount <= 4 && xShape.uniqueRatio <= 0.08 && xShape.uniqueCount <= 60);
  const oneStripedAgainstRepeatedAxis =
    (xShape.uniqueCount <= 40 && yShape.uniqueRatio <= 0.12 && yShape.uniqueCount <= 220) ||
    (yShape.uniqueCount <= 40 && xShape.uniqueRatio <= 0.12 && xShape.uniqueCount <= 220);

  return clamp01(Math.max(
    xNearlyConstant || yNearlyConstant ? 1 : 0,
    bothSmallPalettes ? 0.92 : 0,
    bothRepeatedAxes ? 0.86 : 0,
    oneStripedAgainstRepeatedAxis ? 0.84 : 0,
    oneRepeatedAgainstSmallPalette ? 0.82 : 0,
    Math.max(xShape.dominantRatio, yShape.dominantRatio) >= 0.55 ? 0.74 : 0,
  ));
}

function columnNameInterestingnessWeight(field: string): number {
  const lower = field.toLowerCase();
  if (
    lower.includes("private_attribute") ||
    lower.includes("private-attribute") ||
    lower.includes("__internal") ||
    lower.endsWith("_id") ||
    lower.endsWith(".id")
  ) {
    return 0.02;
  }

  let weight = 1;
  if (lower.includes("operating_condition") || lower.includes("condition.")) weight *= 0.22;
  if (/(^|[._-])(physical_step|time_step|step|iteration|iter|index|case)([._-]|$)/.test(lower)) {
    return Math.min(weight, 0.04);
  }
  if (/(^|[._-])(alpha|beta|mach|omega|rpm)([._-]|$)/.test(lower)) {
    weight *= 0.24;
  }
  return clamp01(weight);
}

function columnValueInterestingnessWeight(values: number[]): number {
  const shape = distributionShape(values);
  if (values.length < 5) return 0.1;
  if (shape.uniqueCount <= 2 || shape.dominantRatio >= 0.82) return 0.04;
  if (shape.uniqueCount <= 12 && shape.uniqueRatio <= 0.12) return 0.18;
  if (shape.uniqueCount <= 40 && shape.uniqueRatio <= 0.06) return 0.24;
  if (shape.uniqueCount <= 90 && shape.uniqueRatio <= 0.08) return 0.38;
  if (shape.dominantRatio >= 0.55) return 0.45;
  return 1;
}

export function columnInterestingnessWeight(rows: Record<string, unknown>[], field: string): number {
  return clamp01(
    columnNameInterestingnessWeight(field) *
    columnValueInterestingnessWeight(extractFinite(rows, field)),
  );
}

export function scoreAlignedRelationship(x: number[], y: number[]): PairRelationshipScore {
  if (x.length < 3 || y.length < 3) {
    return {
      linearR2: 0,
      quadraticR2: 0,
      cubicR2: 0,
      nonlinearLift: 0,
      outlierScore: 0,
      flatLineScore: 0,
      uninteresting: false,
      bestFit: "weak",
      score: 0,
    };
  }

  const linear = clamp01(linearR2(x, y));
  const quadratic = clamp01(quadraticR2(x, y));
  const cubic = clamp01(cubicR2(x, y));
  const nonlinearLift = clamp01(Math.max(quadratic, cubic) - linear);
  const pairOutliers = clamp01(Math.max(
    outlierScore(x),
    outlierScore(y),
    residualOutlierScore(x, y),
  ));
  const flatLineScore = flatLinePairScore(x, y);
  const uninteresting = flatLineScore >= 0.8;

  let bestFit: PairBestFit = "weak";
  if (uninteresting) bestFit = "constant";
  else if (pairOutliers > 0.28 && Math.max(linear, quadratic, cubic) < 0.55) bestFit = "outliers";
  else if (nonlinearLift > 0.16 && cubic > quadratic + 0.03) bestFit = "cubic";
  else if (nonlinearLift > 0.1 && quadratic > linear) bestFit = "quadratic";
  else if (linear > 0.35) bestFit = "linear";
  else if (pairOutliers > 0.18) bestFit = "outliers";
  const rawScore = clamp01(Math.max(linear, quadratic, cubic) * 0.75 + nonlinearLift * 0.15 + pairOutliers * 0.1);

  return {
    linearR2: linear,
    quadraticR2: quadratic,
    cubicR2: cubic,
    nonlinearLift,
    outlierScore: pairOutliers,
    flatLineScore,
    uninteresting,
    bestFit,
    score: uninteresting ? Math.min(rawScore, 0.08) : rawScore,
  };
}

export function scorePairRelationship(
  rows: Record<string, unknown>[],
  xKey: string,
  yKey: string,
): PairRelationshipScore {
  const { x, y } = extractAlignedPairs(rows, xKey, yKey);
  return scoreAlignedRelationship(x, y);
}

// ---------------------------------------------------------------------------
// Column interestingness ranking
// ---------------------------------------------------------------------------

/**
 * Rank columns by aggregate pairwise relationship strength.
 *
 * For each pair of columns, scores the relationship using the chosen type.
 * Each column accumulates its best pairwise score (max, not mean — we want
 * columns that participate in at least one strong relationship).
 *
 * Returns column keys sorted by descending interestingness.
 */
export function rankColumns(
  rows: Record<string, unknown>[],
  columns: ColumnDef[],
  type: RelationshipType,
): string[] {
  const scorer = SCORERS[type];
  const scores = new Map<string, number>();

  for (const col of columns) scores.set(col.field, 0);

  for (let i = 0; i < columns.length; i++) {
    for (let j = i + 1; j < columns.length; j++) {
      const a = columns[i]!;
      const b = columns[j]!;
      const { x, y } = extractAlignedPairs(rows, a.field, b.field);
      if (x.length < 3) continue;

      const score = scorer(x, y);
      scores.set(a.field, Math.max(scores.get(a.field)!, score));
      scores.set(b.field, Math.max(scores.get(b.field)!, score));
    }
  }

  return columns
    .map(c => c.field)
    .sort((a, b) => scores.get(b)! - scores.get(a)!);
}

/**
 * Pick N random columns (Fisher-Yates on a copy).
 */
export function randomColumns(columns: ColumnDef[], n: number): string[] {
  return seededRandomColumns(columns, n, Math.random());
}

export function seededRandomColumns(columns: ColumnDef[], n: number, seed: number): string[] {
  const shuffled = columns.map(c => c.field);
  let state = Math.max(1, Math.floor(Math.abs(seed) * 2147483647)) % 2147483647;
  const next = () => {
    state = (state * 48271) % 2147483647;
    return state / 2147483647;
  };
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(next() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j]!, shuffled[i]!];
  }
  return shuffled.slice(0, Math.min(n, shuffled.length));
}

function outlierScore(values: number[]): number {
  if (values.length < 5) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const quantile = (q: number) => {
    const pos = (sorted.length - 1) * q;
    const lo = Math.floor(pos);
    const hi = Math.ceil(pos);
    const frac = pos - lo;
    return sorted[lo]! * (1 - frac) + sorted[hi]! * frac;
  };
  const q1 = quantile(0.25);
  const q3 = quantile(0.75);
  const iqr = q3 - q1;
  if (iqr <= 0) {
    const median = quantile(0.5);
    const differing = sorted.filter((value) => value !== median);
    if (differing.length === 0) return 0;
    const strongest = differing.reduce((max, value) => Math.max(max, Math.abs(value - median)), 0);
    const scale = Math.max(1, Math.abs(median));
    return differing.length / sorted.length + Math.min(1, strongest / (10 * scale));
  }
  const lo = q1 - 1.5 * iqr;
  const hi = q3 + 1.5 * iqr;
  const outliers = sorted.filter((value) => value < lo || value > hi);
  const strongest = outliers.reduce((max, value) => Math.max(max, Math.max(lo - value, value - hi) / iqr), 0);
  return outliers.length / sorted.length + Math.min(1, strongest / 6);
}

export function rankColumnsByOutliers(
  rows: Record<string, unknown>[],
  columns: ColumnDef[],
): string[] {
  const scores = new Map<string, number>();
  for (const column of columns) {
    scores.set(column.field, outlierScore(extractFinite(rows, column.field)));
  }
  return columns
    .map((column) => column.field)
    .sort((a, b) => scores.get(b)! - scores.get(a)!);
}

export function rankColumnsByCorrelationTo(
  rows: Record<string, unknown>[],
  columns: ColumnDef[],
  targetField: string,
): TargetCorrelationScore[] {
  const scores: TargetCorrelationScore[] = [];
  for (const column of columns) {
    if (column.field === targetField) continue;
    const { x, y } = extractAlignedPairs(rows, column.field, targetField);
    if (x.length < 3) continue;
    const r = pearsonCorrelation(x, y);
    if (!Number.isFinite(r)) continue;
    scores.push({
      field: column.field,
      label: column.headerName,
      r,
      score: Math.abs(r),
      count: x.length,
    });
  }
  return scores.sort((a, b) => {
    const scoreDelta = b.score - a.score;
    if (Math.abs(scoreDelta) > 1e-9) return scoreDelta;
    return b.count - a.count;
  });
}

export function rankColumnsByInterestingness(
  rows: Record<string, unknown>[],
  columns: ColumnDef[],
): string[] {
  const scores = new Map<string, number>();
  const weights = new Map<string, number>();
  const nonConstant = filterConstantColumns(rows, columns);
  for (const column of nonConstant) {
    const weight = columnInterestingnessWeight(rows, column.field);
    weights.set(column.field, weight);
    scores.set(column.field, weight * 0.15 * outlierScore(extractFinite(rows, column.field)));
  }

  for (let i = 0; i < nonConstant.length; i++) {
    for (let j = i + 1; j < nonConstant.length; j++) {
      const a = nonConstant[i]!;
      const b = nonConstant[j]!;
      const aWeight = weights.get(a.field) ?? 0;
      const bWeight = weights.get(b.field) ?? 0;
      const pairWeight = Math.min(aWeight, bWeight);
      if (pairWeight < 0.08) continue;
      const { x, y } = extractAlignedPairs(rows, a.field, b.field);
      if (x.length < 5) continue;
      const relationship = scoreAlignedRelationship(x, y);
      if (relationship.uninteresting) continue;
      const linear = relationship.linearR2;
      const quadratic = relationship.quadraticR2;
      const cubic = relationship.cubicR2;
      const exponential = exponentialR2(x, y);
      const quadraticGain = Math.max(0, quadratic - linear);
      const nonlinearGain = Math.max(0, cubic - linear);
      const score = Math.max(
        linear,
        exponential,
        0.7 * quadratic + 0.3 * quadraticGain,
        0.6 * cubic + 0.4 * nonlinearGain,
      ) * pairWeight;
      scores.set(a.field, Math.max(scores.get(a.field) ?? 0, score));
      scores.set(b.field, Math.max(scores.get(b.field) ?? 0, score));
    }
  }

  return nonConstant
    .map((column) => column.field)
    .sort((a, b) => {
      const scoreDelta = (scores.get(b) ?? 0) - (scores.get(a) ?? 0);
      if (Math.abs(scoreDelta) > 1e-9) return scoreDelta;
      return (weights.get(b) ?? 0) - (weights.get(a) ?? 0);
    });
}

export function selectCorrelogramColumns(
  rows: Record<string, unknown>[],
  columns: ColumnDef[],
  mode: CorrelogramSelectionMode,
  limit: number,
  seed = 1,
  options?: { avoidFlatPairs?: boolean },
): string[] {
  const nonConstant = filterConstantColumns(rows, columns);
  const candidates = nonConstant.length ? nonConstant : columns;
  if (candidates.length <= limit) return candidates.map((column) => column.field);

  let pool = candidates;
  if (options?.avoidFlatPairs) {
    const dataLikeCandidates = candidates.filter((column) => columnInterestingnessWeight(rows, column.field) >= 0.5);
    if (dataLikeCandidates.length >= limit) pool = dataLikeCandidates;
  }

  let ranked: string[];
  if (mode === "random") ranked = seededRandomColumns(pool, pool.length, seed);
  else if (mode === "outliers") ranked = rankColumnsByOutliers(rows, pool);
  else if (mode === "interesting") {
    const dataLikeCandidates = pool.filter((column) => columnInterestingnessWeight(rows, column.field) >= 0.5);
    const interestingPool = dataLikeCandidates.length >= limit ? dataLikeCandidates : pool;
    ranked = rankColumnsByInterestingness(rows, interestingPool);
  } else {
    ranked = rankColumns(rows, pool, mode);
  }

  if (options?.avoidFlatPairs) {
    return selectNonFlatColumnSet(rows, ranked, limit);
  }

  return ranked.slice(0, limit);
}

function selectNonFlatColumnSet(
  rows: Record<string, unknown>[],
  rankedFields: string[],
  limit: number,
): string[] {
  const selected: string[] = [];
  const deferred: string[] = [];

  for (const field of rankedFields) {
    const hasFlatPair = selected.some((existing) => {
      const { x, y } = extractAlignedPairs(rows, existing, field);
      if (x.length < 5) return false;
      return scoreAlignedRelationship(x, y).uninteresting;
    });
    if (hasFlatPair) {
      deferred.push(field);
      continue;
    }
    selected.push(field);
    if (selected.length >= limit) return selected;
  }

  for (const field of deferred) {
    if (!selected.includes(field)) selected.push(field);
    if (selected.length >= limit) break;
  }
  return selected;
}

// ---------------------------------------------------------------------------
// 3D surface interestingness — Z-axis ranking
// ---------------------------------------------------------------------------

/**
 * Extract aligned triples (x, y, z) where all three are finite.
 */
function extractAlignedTriples(
  rows: Record<string, unknown>[],
  xKey: string,
  yKey: string,
  zKey: string,
): { x: number[]; y: number[]; z: number[] } {
  const xs: number[] = [];
  const ys: number[] = [];
  const zs: number[] = [];
  for (const r of rows) {
    const xv = r[xKey], yv = r[yKey], zv = r[zKey];
    if (
      typeof xv === "number" && Number.isFinite(xv) &&
      typeof yv === "number" && Number.isFinite(yv) &&
      typeof zv === "number" && Number.isFinite(zv)
    ) {
      xs.push(xv);
      ys.push(yv);
      zs.push(zv);
    }
  }
  return { x: xs, y: ys, z: zs };
}

/**
 * Score a single Z candidate against one (X, Y) pair.
 *
 * Four signals combined:
 *  1. Multiple R — how well X+Z together predict Y (and vice versa)
 *  2. Partial correlation shift — does Z restructure the X-Y relationship?
 *  3. Non-redundancy — is Z independent from X and Y?
 *  4. Nonlinear surface gain — does Z add curvature?
 */
function scoreZCandidate(
  x: number[],
  y: number[],
  z: number[],
): number {
  if (x.length < 4) return 0;

  // Signal 1: Multiple R (best of predicting Y from X,Z or X from Y,Z)
  const multiR1 = multipleCorrelationR(x, y, z);
  const multiR2 = multipleCorrelationR(y, x, z);
  const multiRScore = Math.max(
    Number.isFinite(multiR1) ? multiR1 : 0,
    Number.isFinite(multiR2) ? multiR2 : 0,
  );

  // Signal 2: Partial correlation shift — |r_xy| vs |r_xy.z|
  const rxy = pearsonCorrelation(x, y);
  const rxyPartial = partialCorrelation(x, y, z);
  const partialShift = (Number.isFinite(rxy) && Number.isFinite(rxyPartial))
    ? Math.abs(Math.abs(rxy) - Math.abs(rxyPartial))
    : 0;

  // Signal 3: Non-redundancy — Z not collinear with X or Y
  const rxz = pearsonCorrelation(x, z);
  const ryz = pearsonCorrelation(y, z);
  const rxz2 = Number.isFinite(rxz) ? rxz * rxz : 0;
  const ryz2 = Number.isFinite(ryz) ? ryz * ryz : 0;
  const nonRedundancy = (1 - rxz2) * (1 - ryz2);

  // Signal 4: Nonlinear gain — quadratic R² minus linear R² for Z vs X and Z vs Y
  const nlGainXZ = Math.max(0, quadraticR2(x, z) - linearR2(x, z));
  const nlGainYZ = Math.max(0, quadraticR2(y, z) - linearR2(y, z));
  const nonlinearGain = Math.max(nlGainXZ, nlGainYZ);

  return (
    0.35 * multiRScore +
    0.25 * partialShift +
    0.25 * nonRedundancy +
    0.15 * nonlinearGain
  );
}

export interface ZCandidateScore {
  field: string;
  score: number;
  label?: string;
}

/**
 * Rank Z-axis candidates for a single (X, Y) pair.
 *
 * Returns candidates sorted by descending "surface interestingness" —
 * how much 3D structure Z adds beyond the 2D X-Y scatter.
 */
export function rankZForPair(
  rows: Record<string, unknown>[],
  xKey: string,
  yKey: string,
  candidates: ColumnDef[],
): ZCandidateScore[] {
  const results: ZCandidateScore[] = [];
  for (const c of candidates) {
    if (c.field === xKey || c.field === yKey) continue;
    const candidateWeight = columnInterestingnessWeight(rows, c.field);
    if (candidateWeight < 0.5) continue;
    const { x, y, z } = extractAlignedTriples(rows, xKey, yKey, c.field);
    const score = scoreZCandidate(x, y, z) * candidateWeight;
    results.push({ field: c.field, score, label: c.headerName ?? c.field });
  }
  return results.sort((a, b) => b.score - a.score);
}

/**
 * Rank Z-axis candidates across all selected column pairs.
 *
 * Strategy: find the "dominant pair" (highest pairwise R²) among the
 * selected columns, then rank Z candidates against that pair.
 * Falls back to averaging across all pairs if no clear dominant pair.
 *
 * Returns candidates sorted by descending surface interestingness.
 */
export function rankZCandidates(
  rows: Record<string, unknown>[],
  selectedColumns: ColumnDef[],
  allCandidates: ColumnDef[],
): ZCandidateScore[] {
  if (selectedColumns.length < 2) return [];

  // Find the dominant pair — the pair with the highest linear R²
  let bestR2 = -1;
  let bestXKey = selectedColumns[0]!.field;
  let bestYKey = selectedColumns[1]!.field;
  const selectedWeights = new Map<string, number>();
  for (const column of selectedColumns) {
    selectedWeights.set(column.field, columnInterestingnessWeight(rows, column.field));
  }

  for (let i = 0; i < selectedColumns.length; i++) {
    for (let j = i + 1; j < selectedColumns.length; j++) {
      const pairWeight = Math.min(
        selectedWeights.get(selectedColumns[i]!.field) ?? 0,
        selectedWeights.get(selectedColumns[j]!.field) ?? 0,
      );
      if (pairWeight < 0.5) continue;
      const { x, y } = extractAlignedPairs(rows, selectedColumns[i]!.field, selectedColumns[j]!.field);
      if (x.length < 3) continue;
      const relationship = scoreAlignedRelationship(x, y);
      if (relationship.uninteresting) continue;
      const r2 = Math.max(relationship.linearR2, relationship.quadraticR2, relationship.cubicR2) * pairWeight;
      if (r2 > bestR2) {
        bestR2 = r2;
        bestXKey = selectedColumns[i]!.field;
        bestYKey = selectedColumns[j]!.field;
      }
    }
  }

  if (bestR2 < 0) {
    const rankedFields = rankColumnsByInterestingness(rows, selectedColumns)
      .filter((field) => (selectedWeights.get(field) ?? 0) >= 0.5);
    if (rankedFields.length < 2) return [];
    bestXKey = rankedFields[0]!;
    bestYKey = rankedFields[1]!;
    bestR2 = 0;
  }

  // If the dominant pair is weak, average across top 3 pairs instead
  const selectedFields = new Set(selectedColumns.map(c => c.field));
  if (bestR2 < 0.1 && selectedColumns.length >= 4) {
    // Gather top pairs
    const pairs: { xKey: string; yKey: string; r2: number }[] = [];
    for (let i = 0; i < selectedColumns.length; i++) {
      for (let j = i + 1; j < selectedColumns.length; j++) {
        const pairWeight = Math.min(
          selectedWeights.get(selectedColumns[i]!.field) ?? 0,
          selectedWeights.get(selectedColumns[j]!.field) ?? 0,
        );
        if (pairWeight < 0.5) continue;
        const { x, y } = extractAlignedPairs(rows, selectedColumns[i]!.field, selectedColumns[j]!.field);
        if (x.length < 3) continue;
        const relationship = scoreAlignedRelationship(x, y);
        if (relationship.uninteresting) continue;
        pairs.push({
          xKey: selectedColumns[i]!.field,
          yKey: selectedColumns[j]!.field,
          r2: Math.max(relationship.linearR2, relationship.quadraticR2, relationship.cubicR2) * pairWeight,
        });
      }
    }
    pairs.sort((a, b) => b.r2 - a.r2);
    const topPairs = pairs.slice(0, 3);

    // Average Z scores across top pairs
    const scoreMap = new Map<string, { total: number; count: number; label?: string }>();
    for (const pair of topPairs) {
      const ranked = rankZForPair(rows, pair.xKey, pair.yKey, allCandidates);
      for (const r of ranked) {
        if (selectedFields.has(r.field)) continue; // don't suggest an already-plotted column
        const entry = scoreMap.get(r.field) ?? { total: 0, count: 0, label: r.label };
        entry.total += r.score;
        entry.count += 1;
        scoreMap.set(r.field, entry);
      }
    }
    return [...scoreMap.entries()]
      .map(([field, { total, count, label }]) => ({ field, score: total / count, label }))
      .sort((a, b) => b.score - a.score);
  }

  // Dominant pair: rank candidates against it
  return rankZForPair(rows, bestXKey, bestYKey, allCandidates)
    .filter(r => !selectedFields.has(r.field));
}

/**
 * Score a specific (X, Y, Z) triplet for surface interestingness.
 * Useful for per-cell badges in the correlogram grid.
 */
export function surfaceScore(
  rows: Record<string, unknown>[],
  xKey: string,
  yKey: string,
  zKey: string,
): number {
  const { x, y, z } = extractAlignedTriples(rows, xKey, yKey, zKey);
  return scoreZCandidate(x, y, z);
}
